import { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import { Col, Row } from "reactstrap";
import ButtonComponent from "../../components/ButtonComponent";
import ClientEquipmentBidirecional from "../../components/ClientEquipmentBidirecional/ClientEquipmentBidirecional";
import InputComponent from "../../components/InputComponent";
import Layout from "../../components/Layout/Layout";
import PageTitle from "../../components/Layout/PageTitle";
import MaintenancePlanAuditTableComponent from "../../components/MaintenancePlans/MaintenancePlanAuditTableComponent";
import { MaintenancePlanNavBar } from "../../components/MaintenancePlans/MaintenancePlanNavBar";
import SelectComponent from "../../components/SelectComponent";
import useValidationSchema from "../../components/Validations";
import { MaintenancePlanUtilities } from "../../helpers/MaintenancePlanUtilities";
import Toast from "../../helpers/Toast";
import { Utilities } from "../../helpers/Utilities";
import { EquipmentDTO } from "../../models/Equipment/EquipmentDTO";
import { EditMaintenancePlanDTO } from "../../models/MaintenancePlan/EditMaintenancePlanDTO";
import { GetMaintenancePlanAuditDTO } from "../../models/MaintenancePlan/GetMaintenancePlanAuditDTO";
import { MaintenancePlanDTO } from "../../models/MaintenancePlan/MaintenancePlanDTO";
import { MaintenancePlanService } from "../../services/MaintenancePlanService";

export type SelectItemsProps = {
  label: string;
  value: string;
};

export default function MaintenancePlanEdit() {
  // props para novo componente
  const [isActiveProjectModule, setIsActiveProjectModule] = useState<boolean>(false);
  const [projectSelected, setProjectSelected] = useState<SelectItemsProps | null>(null);
  const [clientSelected, setClientSelected] = useState<SelectItemsProps | null>(null);
  const [serialNumberSelected, setSerialNumberSelected] = useState<SelectItemsProps | null>(null);
  const [equipment, setEquipment] = useState<EquipmentDTO>({} as EquipmentDTO);
  const [isSearchEquipmentLoading, setIsSearchEquipmentLoading] = useState<boolean>(false);
  const [carPlateSelected, setCarPlateSelected] = useState<SelectItemsProps | null>(null);

  const intl = useIntl();
  const maintenancePlanService = new MaintenancePlanService();
  const [maintenancePlan, setMaintenancePlan] = useState<MaintenancePlanDTO>({} as MaintenancePlanDTO);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoadingEdit, setIsLoadingEdit] = useState<boolean>(false);
  const [isLoadingDeactivate, setIsLoadingDeactivate] = useState<boolean>(false);
  const { id } = useParams<{ id: string }>();

  const [maintenancePlanAudit, setMaintenancePlanAudit] = useState<GetMaintenancePlanAuditDTO>({} as GetMaintenancePlanAuditDTO);

  const [locationType, setLocationType] = useState<string | null>(null);
  const locationTypeList = MaintenancePlanUtilities.getLocationTypeList();


  const [periodicityType, setPeriodicityType] = useState<string | null>(null);
  const periodicityTypeList = MaintenancePlanUtilities.getPeriodicityTypeList(intl);


  const [periodicityRecurrence, setPeriodicityRecurrence] = useState<string | null>(null);
  const periodicityRecurrenceList = MaintenancePlanUtilities.getPeriodicityRecurrenceList();
  const [weeklyPeriodicityRecurrence, setWeeklyPeriodicityRecurrence] = useState<string | null>(null);
  const weeklyPeriodicityRecurrenceList = MaintenancePlanUtilities.getWeeklyPeriodicityRecurrenceList();

  const [periodicityWeekNumber, setPeriodicityWeekNumber] = useState<string | null>(null);
  const periodicityWeekNumberList = MaintenancePlanUtilities.getPeriodicityWeekNumberList(intl);

  const [periodicityDayOfWeek, setPeriodicityDayOfWeek] = useState<string | null>(null);
  const periodicityDayOfWeekList = MaintenancePlanUtilities.getPeriodicityDayOfWeekList(intl);

  const [applicant, setApplicant] = useState<string | null>(null);


  const [temporaryStartDate, setTemporaryStartDate] = useState<string | null>(null);

  // popup
  const [isMaintenancePlanAuditComponentOpen, setIsMaintenancePlanAuditComponentOpen] = useState<boolean>(false);
  const handlePopUpCancel = () => {
    setIsMaintenancePlanAuditComponentOpen(false);
  };

  const GetMaintenancePlan = async () => {
    setIsLoading(true);
    var response = await maintenancePlanService.GetById(Number(id));
    if (response.success === false || response.obj == null) {
      setIsLoading(false);
      Toast.Show("error", response.message);
      return;
    }
    setMaintenancePlan(response.obj);
    setPeriodicityType(response.obj.periodicityType);
    setIsLoading(false);
  };
  useEffect(() => {
    GetMaintenancePlan();
  }, [isLoadingDeactivate]);

  // Validações (campo, tipo dados, mensagem (pt.json))
  const fields = MaintenancePlanUtilities.getValidationFields(periodicityType);
  const schemaClientValidation = useValidationSchema(fields);

  const EditMaintenancePlan = async () => {
    setIsLoadingEdit(true);

    const editMaintenancePlan: EditMaintenancePlanDTO = {
      id: Number(id),

      name: maintenancePlan.name ?? null,
      clientErpId: equipment?.client?.erpId ?? clientSelected?.value ?? null,
      applicantEmail: maintenancePlan?.applicantEmail ?? null,
      equipmentErpId: serialNumberSelected?.value == null ? null : equipment?.erpId ?? null,
      startDate: temporaryStartDate !== null ? new Date(`${temporaryStartDate}`) : maintenancePlan?.startDate,
      locationType: maintenancePlan.locationType ?? null,
      notes: maintenancePlan.notes ?? null,
      expectedHours: Utilities.HandleEmptyValues(maintenancePlan?.expectedHours) ?? null,

      periodicityType: maintenancePlan?.periodicityType,
      periodicityRecurrence: maintenancePlan?.periodicityRecurrence,
      periodicityWeekNumber: maintenancePlan?.periodicityWeekNumber,
      periodicityDayOfWeek: maintenancePlan?.periodicityDayOfWeek
    };

    const { error } = schemaClientValidation.validate(editMaintenancePlan);
    if (error) {
      Toast.Show("error", error.details.map(detail => detail.message).join(', '));
      setIsLoadingEdit(false);
      return;
    }
    const response = await maintenancePlanService.Edit(editMaintenancePlan);

    if (response.success !== true) {
      Toast.Show("error", response.message);
      setIsLoadingEdit(false);
      return;
    }
    Toast.Show("success", response.message);
    setIsLoadingEdit(false);

    setMaintenancePlan({ ...maintenancePlan!, isEdited: true });
  }

  const DeactivateMaintenancePlan = async () => {
    setIsLoadingDeactivate(true);

    const response = await maintenancePlanService.DeactivateMaintenancePlan(Number(id));

    if (response.success !== true) {
      Toast.Show("error", response.message);
      setIsLoadingDeactivate(false);
      return;
    }
    Toast.Show("success", response.message);
    setIsLoadingDeactivate(false);
  };


  const GetMaintenancePlanAudit = async () => {
    setIsLoading(true);
    var response = await maintenancePlanService.GetMaintenancePlanAudit(Number(id));
    if (response.success === false || response.obj == null) {
      setIsLoading(false);
      Toast.Show("error", response.message);
      return;
    }
    setMaintenancePlanAudit(response.obj);
    setIsLoading(false);
  };

  useEffect(() => {
    GetMaintenancePlanAudit();
  }, [maintenancePlan.isEdited]);



  useEffect(() => {
    if (clientSelected === null) {
        setApplicant(null);
        setMaintenancePlan({ ...maintenancePlan, applicantEmail: null });
    }
}, [clientSelected]);



  return (
    <Layout>
      <div className="pageContainer">
        <div className="pageHeader row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div className="col">
            <PageTitle
              title={intl.formatMessage({ id: 'Screens.MaintenancePlans.Edit.Title' })}
              allowGoBack={true}
              dynamicTitle={() => `${intl.formatMessage({ id: 'Screens.MaintenancePlans.Edit.Title' })} - ${maintenancePlan?.numerator}`}
            />
          </div>

          {/* {maintenancePlan.isEdited && (
            <div className="col-auto">
              <ButtonComponent
                disabled={isLoading}
                loading={isLoading}
                text={intl.formatMessage({ id: "Screens.MaintenancePlans.Edit.Audit" })}
                onClick={() => setIsMaintenancePlanAuditComponentOpen(true)}
                style={{ width: "100%" }}
              />
            </div>
          )} */}
        </div>


        <div className="pageBody">
          <Row style={{ margin: "0px", padding: "0px", alignItems: "flex-start", alignContent: "flex-start", display: "flex", flexDirection: "row" }}>
            <Col xl={6} lg={12} md={12} sm={12} xs={12}>

              <ClientEquipmentBidirecional
                isActiveProjectModule={isActiveProjectModule}
                projectSelected={projectSelected}
                clientSelected={clientSelected}
                setClientSelected={setClientSelected}
                serialNumberSelected={serialNumberSelected}
                setSerialNumberSelected={setSerialNumberSelected}
                equipment={equipment}
                setEquipment={setEquipment}
                isSearchEquipmentLoading={isSearchEquipmentLoading}
                setIsSearchEquipmentLoading={setIsSearchEquipmentLoading}

                clientErpId={maintenancePlan.clientErpId}
                equipmentErpId={maintenancePlan.equipmentErpId}

                isActiveSearchByCarPlate={false}
                carPlateSelected={carPlateSelected}
                setCarPlateSelected={setCarPlateSelected}
                disableSoftwareFields={true}
              />

              <Row
                style={{
                  margin: "0px",
                  padding: "0px",
                  alignItems: "flex-end",
                  alignContent: "flex-end",
                }}
              >
                <SelectComponent
                  xl={6}
                  lg={6}
                  md={6}
                  sm={12}
                  xs={12}
                  style={{ width: "100%" }}
                  label={intl.formatMessage({ id: "Screens.MaintenancePlans.Edit.Applicant" })}
                  selectedItemValue={maintenancePlan?.applicantEmail ?? applicant}
                  items={MaintenancePlanUtilities.GetClientApplicants}
                  loadArgs={[clientSelected?.value]}
                  onChange={(applicant: string) => {
                    setMaintenancePlan({ ...maintenancePlan!, applicantEmail: applicant });
                  }}
                />
              </Row>

              <Row
                style={{
                  margin: "0px",
                  padding: "0px",
                  alignItems: "flex-end",
                  alignContent: "flex-end",
                }}
              >
                <InputComponent
                  xl={8}
                  lg={8}
                  md={8}
                  sm={12}
                  xs={12}
                  style={{ width: "100%" }}
                  type={"text"}
                  value={maintenancePlan.name}
                  label={intl.formatMessage({ id: "Screens.MaintenancePlans.Edit.MaintenancePlanName" })}
                  readOnly={false}
                  onChange={(name: string) => {
                    setMaintenancePlan({ ...maintenancePlan!, name: name });
                  }}
                />
              </Row>

              <Row
                style={{
                  margin: "0px",
                  padding: "0px",
                  alignItems: "flex-end",
                  alignContent: "flex-end",
                }}
              >
                <InputComponent
                  xl={2}
                  lg={2}
                  md={2}
                  sm={12}
                  xs={12}
                  style={{ width: "100%" }}
                  type={"date"}
                  value={temporaryStartDate ?? (maintenancePlan?.startDate ? Utilities.formatDateYYYYMMDD(new Date(maintenancePlan?.startDate)) : '')}
                  label={intl.formatMessage({ id: "Screens.MaintenancePlans.Edit.StartDate" })}
                  readOnly={false}
                  onChange={(t: string) => {
                    setTemporaryStartDate(t);
                  }}
                />
              </Row>

              {periodicityType === "Monthly" && (
                <Row
                  style={{
                    margin: "0px",
                    padding: "0px",
                    alignItems: "flex-end",
                    alignContent: "flex-end",
                  }}
                >
                  <SelectComponent
                    xl={3}
                    lg={3}
                    md={3}
                    sm={12}
                    xs={12}
                    style={{ width: "100%" }}
                    label={intl.formatMessage({ id: "Screens.MaintenancePlans.Edit.PeriodicityType" })}
                    selectedItemValue={periodicityType ?? maintenancePlan?.periodicityType}
                    items={periodicityTypeList}
                    onChange={(periodicityType: string) => {
                      setPeriodicityType(periodicityType);
                      setMaintenancePlan({ ...maintenancePlan!, periodicityType: periodicityType });
                    }}
                  />

                  <SelectComponent
                    xl={3}
                    lg={3}
                    md={3}
                    sm={12}
                    xs={12}
                    style={{ width: "100%" }}
                    label={intl.formatMessage({ id: "Screens.MaintenancePlans.Edit.PeriodicityRecurrence" })}
                    selectedItemValue={maintenancePlan?.periodicityRecurrence?.toString() ?? periodicityRecurrence}
                    items={periodicityRecurrenceList ?? []}
                    onChange={(periodicityRecurrence: string) => {
                      setPeriodicityRecurrence(periodicityRecurrence);
                      setMaintenancePlan({ ...maintenancePlan!, periodicityRecurrence: Number(periodicityRecurrence) });
                    }}
                  />

                  <SelectComponent
                    xl={3}
                    lg={3}
                    md={3}
                    sm={12}
                    xs={12}
                    style={{ width: "100%" }}
                    label={intl.formatMessage({ id: "Screens.MaintenancePlans.Edit.PeriodicityWeekNumber" })}
                    selectedItemValue={maintenancePlan?.periodicityWeekNumber ?? periodicityWeekNumber}
                    items={periodicityWeekNumberList ?? []}
                    onChange={(periodicityWeekNumber: string) => {
                      setPeriodicityWeekNumber(periodicityWeekNumber);
                      setMaintenancePlan({ ...maintenancePlan!, periodicityWeekNumber: periodicityWeekNumber });
                    }}
                  />

                  <SelectComponent
                    xl={3}
                    lg={3}
                    md={3}
                    sm={12}
                    xs={12}
                    style={{ width: "100%" }}
                    label={intl.formatMessage({ id: "Screens.MaintenancePlans.Edit.PeriodicityDayOfWeek" })}
                    selectedItemValue={maintenancePlan?.periodicityDayOfWeek ?? periodicityDayOfWeek}
                    items={periodicityDayOfWeekList ?? []}
                    onChange={(periodicityDayOfWeek: string) => {
                      setPeriodicityDayOfWeek(periodicityDayOfWeek);
                      setMaintenancePlan({ ...maintenancePlan!, periodicityDayOfWeek: periodicityDayOfWeek });
                    }}
                  />
                </Row>
              )}

              {periodicityType === "Weekly" && (
                <Row
                  style={{
                    margin: "0px",
                    padding: "0px",
                    alignItems: "flex-end",
                    alignContent: "flex-end",
                  }}
                >
                  <SelectComponent
                    xl={3}
                    lg={3}
                    md={3}
                    sm={12}
                    xs={12}
                    style={{ width: "100%" }}
                    label={intl.formatMessage({ id: "Screens.MaintenancePlans.Edit.PeriodicityType" })}
                    selectedItemValue={periodicityType ?? maintenancePlan?.periodicityType}
                    items={periodicityTypeList}
                    onChange={(periodicityType: string) => {
                      setPeriodicityType(periodicityType);
                      setMaintenancePlan({ ...maintenancePlan!, periodicityType: periodicityType });
                    }}
                  />

                  <SelectComponent
                    xl={3}
                    lg={3}
                    md={3}
                    sm={12}
                    xs={12}
                    style={{ width: "100%" }}
                    label={intl.formatMessage({ id: "Screens.MaintenancePlans.Edit.PeriodicityRecurrence" })}
                    selectedItemValue={maintenancePlan?.periodicityRecurrence?.toString() ?? periodicityRecurrence}
                    items={weeklyPeriodicityRecurrenceList ?? []}
                    onChange={(weeklyPeriodicityRecurrence: string) => {
                      setWeeklyPeriodicityRecurrence(weeklyPeriodicityRecurrence);
                      setMaintenancePlan({ ...maintenancePlan!, periodicityRecurrence: Number(weeklyPeriodicityRecurrence) });
                    }}
                  />

                  <SelectComponent
                    xl={3}
                    lg={3}
                    md={3}
                    sm={12}
                    xs={12}
                    style={{ width: "100%" }}
                    label={intl.formatMessage({ id: "Screens.MaintenancePlans.Edit.PeriodicityDayOfWeek" })}
                    selectedItemValue={maintenancePlan?.periodicityDayOfWeek ?? periodicityDayOfWeek}
                    items={periodicityDayOfWeekList ?? []}
                    onChange={(periodicityDayOfWeek: string) => {
                      setPeriodicityDayOfWeek(periodicityDayOfWeek);
                      setMaintenancePlan({ ...maintenancePlan!, periodicityDayOfWeek: periodicityDayOfWeek });
                    }}
                  />
                </Row>
              )}

              <Row
                style={{
                  margin: "0px",
                  padding: "0px",
                  alignItems: "flex-end",
                  alignContent: "flex-end",
                }}
              >
                <SelectComponent
                  xl={3}
                  lg={3}
                  md={3}
                  sm={12}
                  xs={12}
                  style={{ width: "100%" }}
                  label={intl.formatMessage({ id: "Screens.MaintenancePlans.Edit.LocationType" })}
                  selectedItemValue={maintenancePlan.locationType ?? locationType}
                  items={locationTypeList ?? []}
                  onChange={(t: string) => {
                    setLocationType(t);
                    setMaintenancePlan({ ...maintenancePlan!, locationType: t });
                  }}
                />
                <InputComponent
                  xl={2}
                  lg={2}
                  md={2}
                  sm={12}
                  xs={12}
                  style={{ width: "100%" }}
                  type={"number"}
                  value={maintenancePlan.expectedHours}
                  label={intl.formatMessage({ id: "Screens.MaintenancePlans.Edit.ExpectedHours" })}
                  readOnly={false}
                  onChange={(expectedHours: number) => {
                    setMaintenancePlan({ ...maintenancePlan!, expectedHours: expectedHours });
                  }}
                />
                <InputComponent
                  xl={2}
                  lg={2}
                  md={2}
                  sm={12}
                  xs={12}
                  style={{ width: "100%" }}
                  type={"text"}
                  value={maintenancePlan.status}
                  label={intl.formatMessage({ id: "Screens.MaintenancePlans.Edit.Status" })}
                  readOnly={true}
                />
              </Row>

              <Row
                style={{
                  margin: "0px",
                  padding: "0px",
                  alignItems: "flex-end",
                  alignContent: "flex-end",
                }}
              >
                <InputComponent
                  xl={12}
                  lg={12}
                  md={12}
                  sm={12}
                  xs={12}
                  style={{ width: "100%", height: "120px" }}
                  type={"textArea"}
                  value={maintenancePlan.notes}
                  label={intl.formatMessage({ id: "Screens.MaintenancePlans.Edit.Notes" })}
                  readOnly={false}
                  onChange={(notes: string) => {
                    setMaintenancePlan({ ...maintenancePlan!, notes: notes });
                  }}
                />
              </Row>

              {/* auditoria */}
              {maintenancePlan.isEdited && (
                <Row
                  style={{
                    margin: "0px",
                    padding: "0px",
                    alignItems: "flex-end",
                    alignContent: "flex-end",
                  }}
                >
                  <label>
                    <b>
                      <FormattedMessage id="Screens.MaintenancePlans.Edit.Audit" />
                    </b>
                  </label>

                  <InputComponent
                    xl={4}
                    lg={4}
                    md={4}
                    sm={12}
                    xs={12}
                    style={{ width: "100%" }}
                    type={"text"}
                    value={maintenancePlanAudit.userName}
                    label={intl.formatMessage({ id: "Screens.MaintenancePlans.Edit.Audit.UserName" })}
                    readOnly={true}
                  />

                  <InputComponent
                    xl={4}
                    lg={4}
                    md={4}
                    sm={12}
                    xs={12}
                    style={{ width: "100%" }}
                    type={"date"}
                    value={maintenancePlanAudit?.date ? Utilities.formatDateYYYYMMDD(new Date(maintenancePlanAudit?.date)) : ''}
                    label={intl.formatMessage({ id: "Screens.MaintenancePlans.Edit.Audit.Date" })}
                    readOnly={true}
                  />
                </Row>
              )}


              <Row style={{ marginTop: "10px", paddingLeft: "13px", paddingBottom: "13px", paddingRight: "13px" }}>
                <Col xl={3} lg={3} md={3} sm={6} xs={6}>
                  <ButtonComponent
                    disabled={isLoadingEdit}
                    loading={isLoadingEdit || isLoadingDeactivate || isLoading}
                    text={intl.formatMessage({ id: "Screens.MaintenancePlans.Edit.Button" })}
                    onClick={EditMaintenancePlan}
                    style={{ width: "100%" }}
                  />
                </Col>
                <Col xl={3} lg={3} md={3} sm={6} xs={6}>
                  <ButtonComponent
                    disabled={isLoadingDeactivate}
                    loading={isLoadingDeactivate || isLoadingEdit || isLoading}
                    text={intl.formatMessage({ id: "Screens.MaintenancePlans.Deactivate.Button" })}
                    onClick={DeactivateMaintenancePlan}
                    style={{ width: "100%" }}
                  />
                </Col>
              </Row>
            </Col>

            <Col xl={6} lg={12} md={12} sm={12} xs={12}>
              <MaintenancePlanNavBar />
            </Col>
          </Row>

          {isMaintenancePlanAuditComponentOpen && (
            <MaintenancePlanAuditTableComponent
              IsOpen={isMaintenancePlanAuditComponentOpen}
              OnCancel={handlePopUpCancel}
              MaintenancePlanId={id}
            />
          )}

        </div>
      </div>
    </Layout >
  );
}